import './MediaList.scss';

import { ListItem } from './MediaList';
import { NOTIFICATIONS_COPY } from '@sonar-software/sonar-ts-constants';
import { SonarText } from '@ui/text';
import { useParams } from 'react-router-dom';

import { convertISODateToHumanReadableDate, getDisplayUrl } from '@sonar-software/sonar-ts-lib';
import { coreActions, getInitiativeFile, InitiativeFile, InitiativeLink } from '@sonar-software/sonar-core';
import { DeleteFilled, EditOutlined } from '@ant-design/icons';
import { List, Popconfirm, Row } from 'antd';
import React, { useCallback } from 'react';
import { useAction, useCurrentUser, useDownload } from '@hooks';

interface InitiativesMediaListItemProps {
	listItem: ListItem;
	setSelectedItem: (item: ListItem) => void;
}

export const InitiativeMediaListItem: React.FC<InitiativesMediaListItemProps> = ({ listItem, setSelectedItem }) => {
	const { isReadOnly } = useCurrentUser();
	const isFile = Boolean((listItem as InitiativeFile).contentType);
	const params = useParams<{ id: string }>();
	const initiativeId = Number(params.id);

	const deleteAction = useAction(isFile ?
		coreActions.requestDeleteCoreInitiativesFilesById :
		coreActions.requestDeleteCoreInitiativesLinksById);

	const handleDeleteConfirm = () =>
		deleteAction({
			id: listItem.id,
			initiativeId
		});

	const downloadRequest = useCallback(
		() =>
			getInitiativeFile({
				fileId: listItem.id,
				initiativeId
			}),
		[ initiativeId, listItem.id ]
	);

	const download = useDownload();

	const handleEditClick = () => setSelectedItem(listItem);
	const handleDownloadClick = useCallback(() => {
		download({
			errorMessage: NOTIFICATIONS_COPY.ERROR_DOWNLOAD_INITIATIVE_FILE,
			request: downloadRequest
		});
	}, [ download, downloadRequest ]);

	const renderFile = (file: InitiativeFile) => <a onClick={ handleDownloadClick }>{ file.name }</a>;
	const renderLink = (link: InitiativeLink) =>
		<a
			href={ link.url }
			target='_blank'>
			{ link.title || getDisplayUrl(link.url) }
		</a>;
	const editButton =
		<div key={ 1 }>
			<EditOutlined
				className='edit-button'
				onClick={ handleEditClick } />
		</div>;
	const deleteButton =
		<div key={ 2 }>
			<Popconfirm
				cancelText='Cancel'
				okText='Delete'
				okType='danger'
				onConfirm={ handleDeleteConfirm }
				title='Are you sure?'>
				<DeleteFilled className='delete-button' />
			</Popconfirm>
		</div>;
	const title =
		<Row
			justify='space-between'
			wrap={ false }>
			<SonarText ellipsis>
				{ isFile ? renderFile(listItem as InitiativeFile) : renderLink(listItem as InitiativeLink) }
			</SonarText>
		</Row>;
	const description = <SonarText size={ 12 }>{ convertISODateToHumanReadableDate(listItem.addedOn) }</SonarText>;

	return (
		<List.Item
			actions={ isReadOnly ? [] : [ editButton, deleteButton ] }
			key={ listItem.id }>
			<List.Item.Meta
				description={ description }
				title={ title } />
		</List.Item>
	);
};
